import React from "react";
import { SegmentedControl } from "@amzn/awsui-components-react";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { DashboardViewSelection, SavedViewsManager } from "./SavedViewsManager";
import { Stage } from "../../lusca-search-api/generated-src";
import { DLQ_STAGE_SEGMENTS } from "../../constants";

interface DashboardStageSelectorProps {
  stage: Stage;
  setStage: (stage: Stage) => void;
  getShareUrl: (() => Promise<string>) | null;
  getCompressedFilters: (() => Promise<string>) | null;
  onLoadView: (view: DashboardViewSelection | null) => void;
  activeViewName: string | null;
  setActiveViewName: (name: string | null) => void;
  /** Optional control shown in the middle; Views/share stay at the far right. */
  centerSlot?: React.ReactNode;
  /** Investigation entry points (item lookup drawer, link to the full work queue page). */
  investigationActions?: React.ReactNode;
}

export const DashboardStageSelector: React.FC<DashboardStageSelectorProps> = ({
  stage,
  setStage,
  getShareUrl,
  getCompressedFilters,
  onLoadView,
  activeViewName,
  setActiveViewName,
  centerSlot,
  investigationActions,
}) => {
  // Every group keeps its natural width and wraps to the next row when the dashboard column
  // is narrow (e.g. with the investigations drawer open) instead of squeezing button labels.
  const group: React.CSSProperties = { flex: "0 0 auto" };
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <div style={group}>
        <SegmentedControl
          selectedId={stage}
          onChange={({ detail }) => setStage(detail.selectedId as Stage)}
          options={DLQ_STAGE_SEGMENTS}
        />
      </div>
      {centerSlot && <div style={group}>{centerSlot}</div>}
      <div
        style={{
          ...group,
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
          marginLeft: "auto",
        }}
      >
        {investigationActions}
        <SavedViewsManager
          getFilters={getCompressedFilters}
          stage={stage}
          onLoadView={onLoadView}
          activeViewName={activeViewName}
          setActiveViewName={setActiveViewName}
        />
        <CopyToClipboard
          getContent={getShareUrl || (async () => "")}
          disabled={!getShareUrl}
          iconName="share"
          variant="icon"
          ariaLabel="Copy share link with current filters"
        />
      </div>
    </div>
  );
};
